import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import api from '../utils/api';
import { ArrowLeft, Download, Share2, Printer } from 'lucide-react';

const QRCodesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [eventGuests, setEventGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/events/${id}`)
      .then((res) => {
        setEvent(res.data);
        setEventGuests(res.data.guests || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Event not found</h2>
        <Button onClick={() => navigate('/dashboard/events')}>Back to Events</Button>
      </div>
    );
  }

  const downloadQR = (guestId, guestName, qrCode) => {
    const canvas = document.getElementById(`qr-${guestId}`);
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${guestName.replace(/\s+/g, '_')}_QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downloadAllQR = () => {
    // In real app, this would generate a PDF with all QR codes
    alert('Downloading all QR codes as PDF... (Mock functionality)');
  };

  const printQRCodes = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/dashboard/events/${id}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">QR Codes</h1>
          <p className="text-gray-600 mt-1">{event.name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printQRCodes}>
            <Printer className="w-4 h-4 mr-2" />
            Print All
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={downloadAllQR}>
            <Download className="w-4 h-4 mr-2" />
            Download All
          </Button>
        </div>
      </div>

      {/* Scanner Link Card */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-3">Public Scanner Link</h2>
        <p className="text-sm text-gray-600 mb-4">
          Share this link with your team to scan attendee QR codes
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={`${window.location.origin}/scan/${id}`}
            readOnly
            className="flex-1 px-4 py-2 border rounded-lg bg-gray-50"
          />
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/scan/${id}`);
              alert('Link copied to clipboard!');
            }}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
        </div>
      </Card>

      {/* QR Codes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {eventGuests.map((guest) => (
          <Card key={guest.id} className="p-6 text-center space-y-4 print:break-inside-avoid">
            <div className="flex justify-center">
              <div className="p-4 bg-white border-4 border-gray-200 rounded-lg">
                <QRCodeSVG
                  id={`qr-${guest.id}`}
                  value={guest.qrCode}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>
            <div>
              <p className="font-semibold text-lg">{guest.name}</p>
              <p className="text-sm text-gray-600">{guest.email}</p>
              <p className="text-xs text-gray-500 mt-1">{guest.company}</p>
            </div>
            <div className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
              {guest.qrCode}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => downloadQR(guest.id, guest.name, guest.qrCode)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </Card>
        ))}
      </div>

      {eventGuests.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-gray-500">No guests added yet. Add guests to generate QR codes.</p>
          <Button
            variant="link"
            className="text-orange-500 mt-2"
            onClick={() => navigate(`/dashboard/events/${id}`)}
          >
            Go to event details
          </Button>
        </Card>
      )}
    </div>
  );
};

export default QRCodesPage;